import type { FlowStep, InfoDetail, OperationStatusResponse, UniversalAction } from './types';

export function interpretResponse(response: OperationStatusResponse, infoTitle?: string): FlowStep {
  const status = response.status;
  const action = response.action;

  if (action !== null && action !== undefined) {
    const normalized = normalizeAction(action);
    if (normalized !== null && normalized !== undefined) {
      const details = extractDetails(normalized.extra ?? response.extra);

      if (normalized.type === 'redirect' && typeof normalized.url === 'string') {
        return {
          kind: 'redirect',
          response,
          action: normalized
        };
      }

      if (normalized.type === 'form' && normalized.fields !== null && normalized.fields !== undefined) {
        return {
          kind: 'form',
          response,
          action: normalized
        };
      }

      return {
        kind: 'info',
        response,
        action: normalized,
        details: details.length > 0 ? details : undefined
      };
    }
  }

  const redirectUrl = extractRedirectUrl(response);
  if (redirectUrl !== null && redirectUrl !== undefined) {
    return {
      kind: 'redirect',
      response,
      action: {
        type: 'redirect',
        url: redirectUrl
      }
    };
  }

  const extraDetails = extractDetails(response.extra);
  if (
    extraDetails.length > 0 &&
    (status === 0 || status === 1 || status === undefined || status === null)
  ) {
    return {
      kind: 'info',
      response,
      details: extraDetails,
      action: {
        type: 'show_info',
        title: infoTitle ?? 'Payment information'
      }
    };
  }

  if (status === 2 || status === 6 || status === 7) {
    return { kind: 'success', response };
  }

  if (status === 3 || status === 4) {
    return { kind: 'failed', response };
  }

  if (status === 8) {
    return { kind: 'awaiting-data', response };
  }

  return { kind: 'pending', response };
}

function normalizeAction(action: UniversalAction): UniversalAction | null {
  if (action.type === null || action.type === undefined) {
    return null;
  }

  if (action.type === 'redirect' && typeof action.url === 'string' && action.url.length > 0) {
    return { ...action, url: action.url };
  }

  if (action.type === 'form' && action.fields !== null && action.fields !== undefined && Object.keys(action.fields).length > 0) {
    return { ...action, fields: action.fields };
  }

  if (action.type === 'show_info' || action.type === 'wait' || action.type === 'instruction') {
    return { ...action };
  }

  return { ...action };
}

function extractRedirectUrl(response: OperationStatusResponse): string | null {
  if (response === null || response === undefined || typeof response !== 'object') {
    return null;
  }

  const extra = response.extra;
  if (extra === null || extra === undefined || typeof extra !== 'object') {
    return null;
  }

  const redirect = extra.customer_redirect ?? extra.return_url ?? null;
  if (typeof redirect === 'string' && redirect.length > 0) {
    return redirect;
  }

  return null;
}

function extractDetails(source: Record<string, unknown> | undefined): InfoDetail[] {
  if (source === null || source === undefined || typeof source !== 'object') {
    return [];
  }

  const details: InfoDetail[] = [];

  const visit = (value: unknown, path: string[], depth: number): void => {
    if (depth >= 6) {
      details.push({
        key: path.join('.'),
        label: formatLabel(path),
        value: '[…]'
      });
      return;
    }

    if (value === null || value === undefined) {
      return;
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      details.push({
        key: path.join('.'),
        label: formatLabel(path),
        value: String(value)
      });
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => visit(item, [...path, `[${index}]`], depth + 1));
      return;
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return;
      }
      for (const [key, innerValue] of entries) {
        visit(innerValue, [...path, key], depth + 1);
      }
      return;
    }

    details.push({
      key: path.join('.'),
      label: formatLabel(path),
      value: JSON.stringify(value)
    });
  };

  for (const [key, value] of Object.entries(source)) {
    visit(value, [key], 0);
  }

  return details;
}

function formatLabel(path: string[]): string {
  return path
    .map((segment) => segment.replace(/^[[]|]$/g, '').replace(/_/g, ' '))
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
    .join(' → ');
}

